<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import DataTable from "@/js/vue/components/DataTable.vue";
import {
	IDataTableColumns,
	IDataTablePaginationData,
	IForwarderPoliciesResponseItem,
	ISort,
} from "@/js/types/default";
import { formatDate } from "@/js/utils/date";
import { GqlService } from "@js/services/gql";
import { formatCurrency } from "@/js/utils/currency";

interface IForwarderPolicyRow {
	uId: string;
	policyTitle: string;
	policyNumber: string;
	customerName: string;
	reference: string;
	shipmentType: string;
	premium: number | null;
	claim: number | null;
	status:
		| "Complete"
		| "Closed"
		| "Confirmed"
		| "Incomplete"
		| "Payment awaiting";
	deliveryDate: string | null;
	transportDate: string | null;
	kickbackFee: number | null;
	blindedPolicy: boolean;
	policyUrl?: string;
	currencySymbol: string;
}

interface IPolicyFilters {
	search: string;
}

const dataTableSortValue = ref<ISort>({
	handle: "transportDate",
	variant: "DESC",
});

const dataTableCurrentPage = ref(1);
const dataTableItemsPerPage = ref(10);

const rows = ref<IForwarderPolicyRow[]>([]);
const totalCount = ref(0);

const GET_FORWARDER_POLICIES = `
	query GetForwarderPolicies($limit: Int, $offset: Int, $search: String, $sortField: String, $sortDir: String) {
		forwarderPolicies(limit: $limit, offset: $offset, search: $search, sortField: $sortField, sortDir: $sortDir) {
			total
			limit
			offset
			items {
				policyUId
				policyTitle
				reference
				shipmentType
				status
				transportDate
				shipperName
				premium
				kickbackFee
                currencySymbol
                claim
			}
		}
	}
`;
const isLoading = ref(false);

const fetchPolicies = async () => {
	isLoading.value = true;

	try {
		const json = await GqlService.request(GET_FORWARDER_POLICIES, {
			limit: dataTableItemsPerPage.value,
			offset: (dataTableCurrentPage.value - 1) * dataTableItemsPerPage.value,
			search: appliedFilters.value.search || null,
			sortField: dataTableSortValue.value.handle,
			sortDir: dataTableSortValue.value.variant,
		});

		if (json.errors) {
			console.error(json.errors);
			rows.value = [];
			totalCount.value = 0;
			return;
		}

		const result = json.data?.forwarderPolicies;

		rows.value = (result?.items ?? []).map(
			(item: IForwarderPoliciesResponseItem) => ({
				id: item.policyUId,
				uId: item.policyUId,
				policyTitle: item.policyTitle,
				reference: item.reference,
				shipmentType: item.shipmentType,
				status: item.status,
				transportDate: item.transportDate,
				shipperName: item.shipperName,
				premium: item.premium,
				kickbackFee: item.kickbackFee,
				policyUrl: item.policyUId
					? `/expeditions/policies/${item.policyUId}`
					: undefined,
				currencySymbol: item.currencySymbol,
				claim: item.claim,
			}),
		);

		totalCount.value = result?.total ?? 0;
	} catch (error) {
		console.error(error);
		rows.value = [];
		totalCount.value = 0;
	} finally {
		isLoading.value = false;
	}
};

const draftFilters = reactive<IPolicyFilters>({
	search: "",
});

const appliedFilters = ref<IPolicyFilters>({
	search: "",
});

const columnData: IDataTableColumns[] = [
	{
		key: "policyTitle",
		title: "Policy",
		sortable: true,
		visible: true,
	},
	{
		key: "shipperName",
		title: "Name",
		sortable: true,
		visible: true,
	},
	{
		key: "reference",
		title: "Reference",
		sortable: true,
		visible: true,
	},
	{
		key: "shipmentType",
		title: "Shipment type",
		sortable: true,
		visible: true,
	},
	{
		key: "premium",
		title: "Premium",
		sortable: true,
		visible: true,
	},
	{
		key: "claim",
		title: "Claim",
		sortable: true,
		visible: true,
	},
	{
		key: "status",
		title: "Status",
		sortable: true,
		visible: true,
	},
	{
		key: "transportDate",
		title: "Transport date",
		sortable: true,
		visible: true,
	},
	{
		key: "kickbackFee",
		title: "Kickback fee",
		sortable: true,
		visible: true,
	},
	{
		key: "actions",
		title: "",
		sortable: false,
		visible: true,
	},
];

const dataTablePaginationData = computed<IDataTablePaginationData>(() => {
	const currentCount = rows.value.length;
	const hasNextPage =
		dataTableCurrentPage.value * dataTableItemsPerPage.value < totalCount.value;

	return {
		currentCount,
		hasNextPage,
		totalCount: totalCount.value,
	};
});

const handleApplyFilters = () => {
	appliedFilters.value = { ...draftFilters };
	dataTableCurrentPage.value = 1;
	fetchPolicies();
};

const isPolicyClickable = (data: IForwarderPolicyRow) => {
	return data && data.policyUrl && (data.status.toLowerCase() === 'complete' || data.status.toLowerCase() === 'confirmed');
};

const handleRowClick = (id: string) => {
	const row = rows.value.find((item) => item.uId === id);
	if (!isPolicyClickable(row)) return;

	window.location.href = `${row.policyUrl}?ref=forwarders/policies`;
};

const getShipmentIcon = (shipmentType: IForwarderPolicyRow["shipmentType"]) => {
	switch (shipmentType) {
		case "road":
			return "ri-truck-fill";
		case "sea":
			return "ri-ship-fill";
		case "air":
			return "ri-plane-fill";
		case "storage":
			return "ri-archive-fill";
		default:
			return "ri-shipment-fill";
	}
};

const getStatusType = (status: IForwarderPolicyRow["status"]) => {
	switch (status.toLowerCase()) {
		case "complete":
			return "badge-green";
		case "confirmed":
			return "badge-yellow";
		case "closed":
			return "badge-blue";
		case "payment awaiting":
			return "badge-yellow";
		case "incomplete":
			return "badge-red";
		default:
			return "badge-secondary";
	}
};

watch(dataTableItemsPerPage, () => {
	dataTableCurrentPage.value = 1;
	fetchPolicies();
});

watch(dataTableCurrentPage, () => {
	fetchPolicies();
});

watch(dataTableSortValue, () => {
	dataTableCurrentPage.value = 1;
	fetchPolicies();
});

fetchPolicies();
</script>

<template>
	<div class="policies-search-card p-3 mb-4">
		<div class="policies-search-input-wrapper">
			<input
				v-model="draftFilters.search"
				type="text"
				class="form-control"
				placeholder="Search"
				@keyup.enter="handleApplyFilters"
			/>
		</div>
	</div>

	<DataTable
		:data="rows"
		:pagination-data="dataTablePaginationData"
		:current-page="dataTableCurrentPage"
		:items-per-page="dataTableItemsPerPage"
		:column-data="columnData"
		:sort-by="dataTableSortValue"
		:has-search="false"
		:has-column-selector="false"
		:enable-row-hover="true"
		:is-row-clickable="isPolicyClickable"
		:hide-items-per-page="false"
		@handle-row-click="handleRowClick"
		@handle-sort-value="(value: ISort) => (dataTableSortValue = value)"
		@handle-current-page="(newPage: number) => (dataTableCurrentPage = newPage)"
		@handle-items-per-page="
			(newItemsPerPage: number) => (dataTableItemsPerPage = newItemsPerPage)
		"
	>
		<template #policy="{ rowData }">
			<div>
				<p>
					<small>{{ rowData.policyNumber }}</small>
				</p>
			</div>
		</template>

		<template #shipperName="{ rowData }">
			<div>
				<h6>{{ rowData.shipperName }}</h6>
			</div>
		</template>

		<template #shipmentType="{ rowData }">
			<div>
				<span class="d-inline-flex align-items-center">
					<i :class="getShipmentIcon(rowData.shipmentType)"></i>
					<span class="d-inline-block small ps-1 align-middle">{{
						rowData.shipmentType.toUpperCase()
					}}</span>
				</span>
			</div>
		</template>

		<template #premium="{ rowData }">
			<div>
				<span v-if="rowData.premium !== null">{{
					formatCurrency(rowData.premium, rowData.currencySymbol)
				}}</span>
				<span v-else>-</span>
			</div>
		</template>

		<template #claim="{ rowData }">
			<div>
				<span v-if="rowData.claim !== null">{{
					formatCurrency(rowData.claim, rowData.currencySymbol)
				}}</span>
				<span v-else class="text-muted">-</span>
			</div>
		</template>

		<template #status="{ rowData }">
			<div class="status-cell">
				<span class="badge rounded-pill" :class="getStatusType(rowData.status)">
					{{ rowData.status.toUpperCase() }}
				</span>
			</div>
		</template>

		<template #transportDate="{ rowData }">
			<div>
				<span v-if="rowData.transportDate">{{
					formatDate(rowData.transportDate)
				}}</span>
				<span v-else>-</span>
			</div>
		</template>

		<template #kickbackFee="{ rowData }">
			<div>
				<span
					v-if="rowData.kickbackFee !== null && rowData.kickbackFee > 0"
					class="text-muted"
					>{{
						formatCurrency(rowData.kickbackFee, rowData.currencySymbol)
					}}</span
				>
				<span v-else class="text-muted">-</span>
			</div>
		</template>

		<template #actions="{ rowData }">
			<div class="actions-cell">
				<span
					class="h3 d-flex align-items-center mb-0 ms-2"
					title="Policy"
					data-bs-toggle="tooltip"
					data-bs-placement="left"
					v-if="isPolicyClickable(rowData)"
				>
					<i class="ri-arrow-right-circle-line"></i>
				</span>
			</div>
		</template>
	</DataTable>
</template>

<style scoped>
.status-cell :deep(.badge-green) {
	color: #00b07d;
	background-color: rgba(0, 200, 139, 0.22);
}

.status-cell :deep(.badge-yellow) {
	color: #9a8200;
	background-color: rgba(251, 207, 0, 0.25);
}

.status-cell :deep(.badge-blue) {
	color: #3f5fe0;
	background-color: rgba(72, 106, 233, 0.22);
}

.status-cell :deep(.badge-red) {
	color: #c73646;
	background-color: rgba(220, 53, 69, 0.22);
}

.status-cell :deep(.badge-secondary) {
	color: #5f6770;
	background-color: rgba(108, 117, 125, 0.22);
}
</style>
